import { AxiosError } from 'axios';
import { ITrusstorAxiosError } from '@shared/interfaces/ITrusstorAxiosError';
import { HEADERS, REFRESH_TOKEN_API_PATH } from '@shared/constants/constants';
import { store } from '../store/store';
import { authService, requestService, userService } from '@src/servicesInitializer';
import { IConfig } from '../interfaces/IConfig';
import { IAuthThunkDispatch, logout } from '../store/thunks/login.thunks';
import { handleSessionExpiredAndRedirect } from './bootstrap.utils';

/**
 * The store's `dispatch` as the auth thunks need it. `store` is annotated `EnhancedStore`, an
 * annotation that erases the thunk middleware from `dispatch`'s type; the 403 branch below used to
 * carry a `@ts-ignore` for that. Same view `bootstrap.utils` takes.
 */
const dispatchAuthThunk: IAuthThunkDispatch = store.dispatch as unknown as IAuthThunkDispatch;

export const isErrorResponseIsFromBackend = (error: AxiosError<any>, config: IConfig): boolean =>
	!!error.config?.url?.startsWith(config.apiGateway);

export const isRefreshTokenRequest = (error: ITrusstorAxiosError): boolean =>
	!!error.config.url?.includes(REFRESH_TOKEN_API_PATH);

export const handleUnauthorizedResponse = async (error: ITrusstorAxiosError) => {
	if (!error.config) {
		return Promise.reject(error);
	}

	// The refresh request itself must never be retried through the refresh flow - it would await its own promise
	if (isRefreshTokenRequest(error)) {
		handleSessionExpiredAndRedirect();
		return Promise.reject(error);
	}

	if (error.config.retryCount === 3) {
		handleSessionExpiredAndRedirect();
		return Promise.reject(error);
	}

	let isRefreshed: boolean = false;
	try {
		isRefreshed = await authService.refreshUserToken();
	} catch (refreshError) {
		isRefreshed = false;
	}

	if (isRefreshed) {
		// eslint-disable-next-line no-param-reassign
		error.config.retryCount = (error.config.retryCount || 0) + 1;
		// eslint-disable-next-line no-param-reassign
		error.config.headers.set(HEADERS.authorization, `Bearer ${userService.getLoggedUserAccessToken()}`);
		return requestService.resendRequest(error.config);
	}

	handleSessionExpiredAndRedirect();
	return Promise.reject(error);
};

export const errorInterceptor = (config: IConfig) => async (error: AxiosError<any>) => {
	if (!isErrorResponseIsFromBackend(error, config)) {
		return Promise.reject(error);
	}

	switch (error?.response?.status) {
		case 401:
			return handleUnauthorizedResponse(error as ITrusstorAxiosError);
		case 403:
			dispatchAuthThunk(logout());
			return Promise.reject(error);
		case 418:
			window.location.reload();
			break;
		default:
			return Promise.reject(error);
	}

	return undefined;
};
